import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar = ({ transparent = false, glass = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Properties', href: '/properties' },
    { name: 'Services', href: '/services' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  // Helper to determine text color class
  const getTextColor = () => {
    if (glass) return 'text-primary';
    if (transparent) return 'text-[#F5F0E8]';
    return 'text-white';
  };

  const getHoverColor = () => {
    if (glass) return 'hover:text-amber-600';
    return 'hover:text-[#F59E0B]';
  }

  // Helper to determine active text color class
  const getActiveColor = () => {
    if (glass) return 'text-amber-600';
    return 'text-[#F59E0B]';
  };

  return (
    <nav className={`
      ${transparent ? 'absolute' : 'fixed'} 
      top-0 left-0 right-0 z-50 
      ${!transparent && !glass && 'bg-primary'}
      ${glass && 'bg-white/70 backdrop-blur-md border-b border-white/20 shadow-sm'}
    `}>
      <div className="max-w-7xl mx-auto px-2 md:px-4 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-4 group mt-4" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_realty-vision-4/artifacts/xju4cezs_sara-realty-logo.png"
              alt="Sara Realty"
              className="h-16 w-auto drop-shadow-2xl"
            />
            <span className={`font-heading text-3xl font-medium ${getTextColor()} drop-shadow-lg`}>Sara Realty</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase()}`}
                className={`text-base font-medium transition-all duration-300 ${isActive(item.href)
                  ? `${getActiveColor()} drop-shadow-lg scale-105`
                  : `${getTextColor()} ${getHoverColor()} drop-shadow-md hover:scale-105`
                  }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-white"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-primary/95 backdrop-blur-lg border-t border-white/10"
          >
            <div className="px-6 py-4 space-y-3">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                  className={`block py-2 text-base font-medium transition-colors ${isActive(item.href) ? 'text-accent' : 'text-white/90'
                    }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};